import type { PublicCompany } from '~/types/person/companies';
import type { CompanyType as CompanyTypeValue } from '~/types/person/details';

/** Shorter queries match too much to be useful, and every keystroke costs an API call. */
export const OTHER_COMPANIES_MIN_QUERY_LENGTH = 3;

/**
 * A search returning fewer than this many monitored data brokers has left the member with a
 * mostly-empty table, so the companies we track outside their broker list are worth surfacing.
 */
export const OTHER_COMPANIES_BROKER_THRESHOLD = 5;

/** A 2x3 grid. */
export const OTHER_COMPANIES_LIMIT = 6;

/** How long the lookup waits after the search stops changing before it asks, so a member typing costs one request. */
export const OTHER_COMPANIES_LOOKUP_DELAY_MS = 500;

/**
 * Over-fetch per page: monitored companies and non-actionable types are filtered out client-side,
 * so a page sized to the grid would routinely come up short.
 */
export const OTHER_COMPANIES_PAGE_SIZE = 20;

/**
 * Ceiling on pages walked while looking for six actionable results. A member monitoring a large
 * share of a common prefix can exhaust several pages; this stops that from becoming an unbounded
 * request storm for a query that will never fill the grid.
 */
export const OTHER_COMPANIES_MAX_PAGES = 5;

/**
 * Types a member can't act on from this page: internal bookkeeping rows, placeholder records,
 * and companies we deliberately hold no PII for. Everything else — including data brokers the
 * member isn't monitoring — is fair game, so nothing they searched for silently disappears.
 */
export const OTHER_COMPANIES_EXCLUDED_TYPES: readonly CompanyTypeValue[] = [
    'nopii',
    'affiliate',
    'sandbox',
    'non_public',
    'action',
    'blur_location',
];

export interface OtherCompaniesVisibility {
    searchQuery: string;
    selectedType: string;
    selectedStatus: string;
    /** How many monitored data brokers the current search matched. */
    brokerMatchCount: number;
}

export interface OtherCompaniesSelection {
    results: PublicCompany[];
    /** Every company already monitored for this member, matched or not. */
    monitoredCompanyIds: number[];
    limit?: number;
}

/** The Type/Status dropdowns treat both '' and 'all' as unset. */
const isFilterActive = (value: string) => Boolean(value) && value !== 'all';

const isExcludedType = (company: PublicCompany) =>
    OTHER_COMPANIES_EXCLUDED_TYPES.includes(company.type?.value as CompanyTypeValue);

/**
 * Whether to show companies from outside the member's monitored broker list.
 *
 * Only ever answers "we couldn't find that company", never "your filter excluded it" — an active
 * Type or Status filter suppresses the grid entirely. Status especially: an unmonitored company
 * has no exposure status, so pairing "no Discovered brokers" with six unrelated cards would read
 * as a contradiction.
 */
export const shouldShowOtherCompanies = ({
    searchQuery,
    selectedType,
    selectedStatus,
    brokerMatchCount,
}: OtherCompaniesVisibility): boolean => {
    if (isFilterActive(selectedType) || isFilterActive(selectedStatus)) {
        return false;
    }

    if (searchQuery.trim().length < OTHER_COMPANIES_MIN_QUERY_LENGTH) {
        return false;
    }

    return brokerMatchCount < OTHER_COMPANIES_BROKER_THRESHOLD;
};

/**
 * Narrows raw search results to the cards worth rendering: drops anything already monitored
 * (it belongs in the table above, not down here), drops non-actionable types, de-duplicates,
 * and caps the grid.
 */
export const selectOtherCompanies = ({
    results,
    monitoredCompanyIds,
    limit = OTHER_COMPANIES_LIMIT,
}: OtherCompaniesSelection): PublicCompany[] => {
    const monitored = new Set(monitoredCompanyIds);
    const seen = new Set<number>();
    const selected: PublicCompany[] = [];

    for (const company of results) {
        if (selected.length >= limit) {
            break;
        }

        if (monitored.has(company.id) || seen.has(company.id) || isExcludedType(company)) {
            continue;
        }

        seen.add(company.id);
        selected.push(company);
    }

    return selected;
};
